import { Mail, MessageSquare } from "lucide-react";
import type { ComponentType } from "react";
import {
  FacebookIcon,
  InstagramIcon,
  LineIcon,
  LinkedInIcon,
  MessengerIcon,
  TelegramIcon,
  TikTokIcon,
  WhatsAppIcon,
  XIcon,
  YouTubeIcon,
} from "@/components/admin_dashboard/BrandIcons";
import { cn } from "@/lib/utils";

type ChannelIconProps = { className?: string; size?: number };

type ChannelMeta = {
  label: string;
  Icon: ComponentType<ChannelIconProps>;
};

const EmailIcon = ({ className, size = 24 }: ChannelIconProps) => (
  <Mail className={className} size={size} />
);

const GenericChannelIcon = ({ className, size = 24 }: ChannelIconProps) => (
  <MessageSquare className={className} size={size} />
);

const CHANNEL_META: Record<string, ChannelMeta> = {
  whatsapp: { label: "WhatsApp", Icon: WhatsAppIcon },
  whatsapp_e164: { label: "WhatsApp", Icon: WhatsAppIcon },
  telegram: { label: "Telegram", Icon: TelegramIcon },
  telegram_chat_id: { label: "Telegram", Icon: TelegramIcon },
  instagram: { label: "Instagram", Icon: InstagramIcon },
  instagram_psid: { label: "Instagram", Icon: InstagramIcon },
  facebook: { label: "Facebook", Icon: FacebookIcon },
  facebook_messenger: { label: "Messenger", Icon: MessengerIcon },
  facebook_psid: { label: "Messenger", Icon: MessengerIcon },
  line: { label: "LINE", Icon: LineIcon },
  line_user_id: { label: "LINE", Icon: LineIcon },
  tiktok: { label: "TikTok", Icon: TikTokIcon },
  x: { label: "X (Twitter)", Icon: XIcon },
  linkedin: { label: "LinkedIn", Icon: LinkedInIcon },
  youtube: { label: "YouTube", Icon: YouTubeIcon },
  email: { label: "Email", Icon: EmailIcon },
};

const getChannelMeta = (channel: string | null | undefined): ChannelMeta => {
  const normalized = channel?.trim().toLowerCase() ?? "";
  if (CHANNEL_META[normalized]) return CHANNEL_META[normalized];

  const label = normalized
    ? normalized.replace(/[_-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase())
    : "—";
  return { label, Icon: GenericChannelIcon };
};

type ChannelLabelProps = {
  channel: string | null | undefined;
  className?: string;
  iconClassName?: string;
  iconSize?: number;
};

export const ChannelLabel = ({
  channel,
  className,
  iconClassName,
  iconSize = 16,
}: ChannelLabelProps) => {
  const { label, Icon } = getChannelMeta(channel);

  if (!channel) return <span className={className}>—</span>;

  return (
    <span className={cn("inline-flex items-center gap-2", className)}>
      <Icon className={cn("shrink-0", iconClassName)} size={iconSize} />
      <span>{label}</span>
    </span>
  );
};
